import { useEffect, useState } from "react";

import {
  Autocomplete,
  MultiTypeDownloadToolTip,
  SelectData,
  Switch,
  TextField,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { ExportFiltersData, ExportTypeEnum } from "@king-ict/eupisi-ui/models";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, Redirect } from "react-router";

import {
  createEnrollmentGroupCriterion,
  createInstitutionCriterion,
  getCriteria,
  getPedagogicalYearCriteriaExportFile,
  getEnrollmentGroupCriterion,
  getInstitutionCriterion,
  updateEnrollmentGroupCriterion,
  updateInstitutionCriterion,
} from "../../../api";
import { criteriaRoute, useTypedSelector } from "../../../app";
import { AdministrationCreateUpdate } from "../../../components";
import { useBusinessRule } from "../../../hooks";
import {
  Criterion,
  InstitutionCriterion,
  InstitutionCriterionValidationSchema,
  isUserInEnrollmentGroup,
} from "../../../models";
import styles from "./CriterionCreateUpdate.module.scss";

export const CriterionCreateUpdate = () => {
  const { t } = useTranslation();

  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [criterion, setCriterion] = useState<InstitutionCriterion>({
    isDirectEnrollment: false,
  } as InstitutionCriterion);

  const user = useTypedSelector((s) => s.application.user);
  const pedagogicalYear = useTypedSelector(
    (s) => s.application.pedagogicalYear
  );

  const { id } = useParams<{ id: string }>();

  const { canCreateCriterion, canUpdateCriterion } = useBusinessRule();

  const [exportType, setExportType] = useState<number>(ExportTypeEnum.Excel);

  useEffect(() => {
    (async () => {
      setCriteria(await getCriteria());
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (user && id) {
        isUserInEnrollmentGroup(user)
          ? setCriterion(await getEnrollmentGroupCriterion(+id))
          : setCriterion(await getInstitutionCriterion(+id));
      }
    })();
  }, [user, id]);

  const handleSave = async (
    isCreation: boolean,
    institutionCriterion: InstitutionCriterion
  ) => {
    if (isUserInEnrollmentGroup(user)) {
      isCreation
        ? await createEnrollmentGroupCriterion(institutionCriterion)
        : await updateEnrollmentGroupCriterion(institutionCriterion);
    } else {
      isCreation
        ? await createInstitutionCriterion(institutionCriterion)
        : await updateInstitutionCriterion(institutionCriterion);
    }

    return true;
  };

  const type = id ? "update" : "create";

  const handleDownloadDocument = () => {
    getPedagogicalYearCriteriaExportFile({
      pedagogicalYearId: pedagogicalYear.id,
      exportTypeId: exportType,
    } as ExportFiltersData);
  };

  const handleChangeFormat = (typeId: number) => {
    setExportType(typeId);
  };

  return canCreateCriterion(pedagogicalYear?.id) ||
    canUpdateCriterion(pedagogicalYear?.id) ? (
    <>
      <AdministrationCreateUpdate
        type={type}
        data={criterion}
        backUrl={criteriaRoute}
        backText={t("criterion.createUpdateBackText")}
        title={t("criterion.createUpdateTitle")}
        validationSchema={InstitutionCriterionValidationSchema}
        onSave={handleSave}
      >
        {(state, errors, onFieldChange) => (
          <>
            <Row className={styles.rowSpace}>
              <Col xs={12}>
                <Typography variant="baseBold" className={styles.subTitle}>
                  {`${t("common.pedagogicalYear")} ${
                    pedagogicalYear?.name || ""
                  }`}
                </Typography>
              </Col>
              {type === "create" && (
                <Col md={9}>
                  <Autocomplete
                    label={t("criterion.criterion")}
                    name={nameof<typeof state>("criterionId")}
                    value={state?.criterionId}
                    onChange={onFieldChange}
                    selectData={
                      {
                        valueProp: "id",
                        textProp: "name",
                        data: criteria || [],
                      } as SelectData<Criterion>
                    }
                    errorText={errors?.criterionId}
                  />
                </Col>
              )}
              {type === "update" && (
                <Col md={12}>
                  <Typography variant="base" className={styles.TextField}>
                    {`${criterion.criterionName}`}
                  </Typography>
                </Col>
              )}
              <Col md={3}>
                {!state.isDirectEnrollment && (
                  <TextField
                    type="number"
                    label={`${t("criterion.points")}`}
                    name={nameof<typeof state>("points")}
                    value={
                      state?.points !== undefined && state?.points !== null
                        ? state?.points
                        : ""
                    }
                    min={0}
                    max={100}
                    maxLength={3}
                    onChange={onFieldChange}
                    errorText={errors?.points}
                  />
                )}
              </Col>
            </Row>
            <Row className={styles.rowSpace}>
              <Col xs={12}>
                <Switch
                  ariaLabel={t("criterion.createUpdateDirectEnrollment")}
                  label={t("criterion.createUpdateDirectEnrollment")}
                  name={nameof<typeof state>("isDirectEnrollment")}
                  selected={state?.isDirectEnrollment || false}
                  onChange={onFieldChange}
                />
              </Col>
            </Row>
          </>
        )}
      </AdministrationCreateUpdate>

      <MultiTypeDownloadToolTip
        exportType={exportType}
        onclick={handleDownloadDocument}
        changeFormat={handleChangeFormat}
      />
    </>
  ) : (
    <Redirect to="/page-not-available" />
  );
};
